export type CabinetScenario = "normal" | "empty" | "error" | "loading" | "long" | "no_avatar" | "multi_currency" | "blocked";

export interface CabinetLoadOptions {
  scenario?: CabinetScenario;
  signal?: AbortSignal;
}

export class CabinetSourceError extends Error {
  constructor(readonly source: "profile" | "wallet" | "orders" | "dialogs" | "account", readonly requestId = "") {
    super(source);
    this.name = "CabinetSourceError";
  }
}

export interface ProfileContact {
  value: string;
  verified: boolean;
}

export interface UserProfileSnapshot {
  displayName: string;
  firstName: string;
  lastName?: string;
  birthDate?: string;
  email?: ProfileContact;
  phone?: ProfileContact;
  editableFields: UserProfileField[];
  avatarFileId?: string;
  avatarUrl?: string;
  avatarAlt: string;
  version: number;
}

export type UserProfileField = "first_name" | "last_name" | "birth_date";

export interface UserProfileUpdate {
  expectedVersion: number;
  firstName?: string;
  lastName?: string | null;
  birthDate?: string | null;
}

export interface AvatarMutationResult {
  avatarFileId?: string;
  avatarUrl?: string;
  version: number;
}

export interface PasswordChange {
  currentPassword: string;
  newPassword: string;
}

export interface EmailVerificationChallenge {
  resendAfter: string;
  expiresAt: string;
}

export interface WalletBalance {
  currency: string;
  amountMinor: string;
}

export interface LedgerItem {
  id: string;
  title: string;
  amountMinor: string;
  currency: string;
  occurredAt: string;
}

export interface PaymentItem {
  id: string;
  amountMinor: string;
  currency: string;
  state: "pending" | "succeeded" | "failed";
  createdAt: string;
}

export interface WalletSnapshot {
  balances: WalletBalance[];
  ledger: LedgerItem[];
  payments: PaymentItem[];
}

export type OrderState = "awaiting_payment" | "paid" | "in_progress" | "completed" | "cancelled";
export type OrderAction = "open_dialog" | "continue_call" | "request_refund";

export interface ConsultationOrderSummary {
  id: string;
  expertName: string;
  state: OrderState;
  amountMinor: string;
  currency: string;
  durationMinutes: number;
  remainingMinutes: number;
  scheduledAt?: string;
  createdAt: string;
  allowedActions: OrderAction[];
}

export interface ConsultationOrdersSnapshot {
  orders: ConsultationOrderSummary[];
}

export type {DialogDirectorySnapshot, DialogMessage, DialogSummary, DialogThread} from "../dialogs/model";

export type DeletionBlocker = "wallet_balance_non_zero" | "wallet_hold_active" | "financial_settlement_pending" | "payment_pending" | "consultation_order_active" | "booking_active" | "payout_pending" | "owner_unavailable";
export type DeletionNextAction = "wallet" | "orders" | "support" | "retry";

export interface AccountDeletionPreview {
  blockers: DeletionBlocker[];
  confirmationHint: string;
  operationId?: string;
  handleExpiresAt?: string;
  nextActions?: Partial<Record<DeletionBlocker, DeletionNextAction>>;
}

export interface AccountDeletionConfirm {
  operationId: string;
  contactKind: "email" | "phone";
  contact: string;
  key: string;
}

export interface AccountDeletionResult {
  operationId: string;
  state: "processing" | "blocked" | "retryable" | "anonymized";
  blockers: DeletionBlocker[];
  nextActions: Partial<Record<DeletionBlocker, DeletionNextAction>>;
  processingPhase?: "preparing" | "sealing" | "releasing" | "committing";
  retryReason?: "owner_unavailable" | "prepare_expired" | "operation_conflict";
  anonymizedAt?: string;
}
